import React, { useEffect } from 'react';
import LogoBannerTag from './LogoBannerTag';
import { styles } from './TitleSection.css';
import { Title } from '@jbhi-fi/components/src/TitleSection/components/Title';
import { ArtistLink } from '@jbhi-fi/components/src/TitleSection/components/ArtistLink';
import { useProductAccordion, goToAccordion } from '../AccordionContext';
import { useIdPrefix } from '@jbhi-fi/components/src/IdPrefix';
import {
  MarketplaceSellerCallout,
  MarketplaceSellerCalloutProps,
} from '@jbhi-fi/components/src/Marketplace/MarketplaceSellerCallout/MarketplaceSellerCallout';
import { initializeBazaarvoice } from '../utils/initializeBazaarvoice';
import { PromoTag } from '@jbhi-fi/components/src/ProductCard/utils/getTags';
import { LimitedRunPromo } from '@jbhi-fi/apis/src/shopify/types/OnlineProduct';

export type TitleProps = {
  className?: string;
  plu?: string;
  code?: string;
  sku?: string;
  title?: string;
  numberOfReviews?: number;
  averageReviews?: number;
  logo?: string;
  brand?: string;
  productFlags?: string;
  availability?: boolean;
  preOrder?: boolean;
  isComingSoon?: boolean;
  isDigitalPreOrder?: boolean;
  artist?: string;
  productDepartment?: number;
  pctRating?: number;
  showReviews?: boolean;
  rating?: string | number;
  pdpDepartmentArtistIds?: Array<string>;
  pdpDepartmentTitleIds?: Array<string>;
  softwareFormat?: string;
  limitedRunPromo?: LimitedRunPromo;
  isPerksMember?: boolean;
  bannerLabel?: string;
  marketplaceSeller?: Omit<MarketplaceSellerCalloutProps, 'className' | 'testId'>;
  promoTags?: Array<PromoTag> | null;
};

export const TitleSection = ({
  className,
  plu,
  code,
  sku,
  title,
  logo,
  brand,
  artist,
  productDepartment,
  showReviews,
  pdpDepartmentArtistIds,
  pdpDepartmentTitleIds,
  softwareFormat,
  limitedRunPromo,
  isComingSoon,
  isPerksMember,
  bannerLabel,
  marketplaceSeller,
  promoTags,
}: TitleProps): JSX.Element => {
  const hasTabs = window.location.href.includes('view=tabs');
  const useAccordion = useProductAccordion();
  const isArtist =
    !logo && artist && pdpDepartmentArtistIds?.some(item => item.trim() === productDepartment?.toString());

  const handleClick = () => {
    if (!hasTabs) {
      goToAccordion('reviews', useAccordion);
    }
  };

  useEffect(() => {
    if (showReviews) {
      const handleBVRatingSummaryClick = () => {
        window.BV.swat_reviews.on('show', () => {
          handleClick && handleClick();
        });
      };

      const setupBVCallback = () => {
        if (window.BV && window.BV.swat_reviews) {
          window.bvCallback = handleBVRatingSummaryClick;
          return true;
        }
        return false;
      };

      const onLoad = () => {
        if (!setupBVCallback()) {
          console.log('BV library loaded but swat_reviews not available');
        }
      };
      const onError = () => {
        console.log('BV error');
      };

      if (!setupBVCallback()) {
        initializeBazaarvoice(onLoad, onError);
      }
    }
  }, []);

  const elementIds = {
    wrapper: useIdPrefix('title-section'),
    reviews: useIdPrefix('ratings-reviews'),
    modelSkuWrapper: useIdPrefix('title-model-sku-wrapper'),
    model: useIdPrefix('title-model'),
    sku: useIdPrefix('title-sku'),
    plu: useIdPrefix('title-plu'),
  };

  return (
    <div className={className} id={elementIds.wrapper}>
      <LogoBannerTag
        productDepartment={productDepartment}
        bannerLabel={bannerLabel}
        logo={logo}
        brand={brand}
        pdpDepartmentTitleIds={pdpDepartmentTitleIds}
        softwareFormat={softwareFormat}
        limitedRunPromo={limitedRunPromo}
        isComingSoon={isComingSoon}
        isPerksMember={isPerksMember}
        promoTags={promoTags}
      />
      <Title title={title} />
      {isArtist ? <ArtistLink artist={artist} /> : null}
      <div className={styles.productMetaContainer}>
        {showReviews ? (
          <div id={elementIds.reviews || undefined} data-testid={elementIds.reviews}>
            <div data-bv-show="rating_summary" data-bv-product-id={sku ?? ''}></div>
          </div>
        ) : null}
        <div className={styles.modelSkuContainer} id={elementIds.modelSkuWrapper}>
          <div className={styles.productMeta} id={elementIds.model}>
            MODEL: {code}
          </div>
          <div className={styles.productMeta} id={elementIds.sku}>
            SKU: {sku}
          </div>
        </div>
        {marketplaceSeller ? (
          <MarketplaceSellerCallout
            className={styles.marketplaceCallout}
            testId="marketplace-seller_callout_pdp"
            {...marketplaceSeller}
          />
        ) : null}
      </div>
      <div className={styles.externalPlu} id={elementIds.plu}>
        PLU: {plu}
      </div>
    </div>
  );
};

export default TitleSection;
